import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { API_BASE_URL } from '../../../utils/config';

const PRICING_API = `${API_BASE_URL}/api/wordpress/pricing`;

/**
 * Fired by the Pricing page the moment an Apply request is accepted.
 *
 * The banner polls, and while idle it waits 20s between polls. A formula
 * started just after a poll therefore stayed invisible for up to 20 seconds —
 * the toast said progress was shown at the top, but there was nothing there
 * yet. This event lets the page tell the banner directly instead of waiting
 * for the next poll to stumble across the job.
 *
 * A window event rather than shared state because the banner is mounted from
 * AdminLayout while the Pricing page sits deep inside the router; they have no
 * common owner short of a global store.
 */
export const PRICING_JOB_EVENT = 'marketsync:pricing-job-started';

export const notifyPricingJobStarted = (formula) => {
    window.dispatchEvent(new CustomEvent(PRICING_JOB_EVENT, {
        detail: {
            formula_id: formula && (formula._id || formula.formula_id),
            name: (formula && formula.name) || 'formula',
        },
    }));
};

/**
 * Global progress bar for pricing-formula runs.
 *
 * Applying a formula can touch hundreds of products and push each one to
 * WordPress, so it now runs as a background job on the server. This banner
 * polls the job list and sits below MainNav (rendered from AdminLayout), which
 * means the user can leave the Pricing page — or the Products page entirely —
 * and still see how far the run has got.
 *
 * Polls only while something is running, then once more to catch the final
 * result, so an idle admin session costs one request every 20 seconds.
 */
const PricingJobBanner = () => {
    const { token } = useSelector((s) => s.auth);
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
    const [jobs, setJobs] = useState([]);
    const [dismissed, setDismissed] = useState([]);
    // Placeholder shown between "Apply accepted" and the first poll that sees
    // the job, so the bar appears on the same click rather than a round-trip
    // later. Replaced by real server numbers as soon as they arrive.
    const [pending, setPending] = useState(null);

    const aliveRef   = useRef(true);
    const timerRef   = useRef(null);
    const pendingRef = useRef(null);

    const clearTimer = () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };

    const tick = useCallback(async () => {
        clearTimer();
        try {
            const res = await axios.get(`${PRICING_API}/formulas/jobs`, { headers });
            if (!aliveRef.current) return;
            const list = res.data?.jobs || [];
            setJobs(list);

            // Drop the placeholder once the server reports this formula.
            const waiting = pendingRef.current;
            if (waiting && list.some((j) => String(j.formula_id) === String(waiting.formula_id))) {
                setPending(null);
            }

            // Fast while a run is in flight, slow when idle.
            timerRef.current = setTimeout(tick, list.some((j) => j.status === 'running') ? 1500 : 20000);
        } catch {
            if (aliveRef.current) timerRef.current = setTimeout(tick, 30000);
        }
    }, [headers]);

    useEffect(() => { pendingRef.current = pending; }, [pending]);

    useEffect(() => {
        if (!token) return undefined;
        aliveRef.current = true;
        tick();
        return () => { aliveRef.current = false; clearTimer(); };
    }, [token, tick]);

    // Apply was just accepted — show the bar now and poll immediately instead
    // of waiting out the idle interval.
    useEffect(() => {
        const onStart = (e) => {
            const d = (e && e.detail) || {};
            setPending({
                formula_id: d.formula_id, name: d.name || 'formula',
                status: 'running', processed: 0, total: 0,
                updated: 0, skipped: 0, failed: 0,
            });
            setDismissed((prev) => prev.filter((id) => String(id) !== String(d.formula_id)));
            tick();
        };
        window.addEventListener(PRICING_JOB_EVENT, onStart);
        return () => window.removeEventListener(PRICING_JOB_EVENT, onStart);
    }, [tick]);

    // Safety net: if the job never shows up (request rejected, server restart),
    // the placeholder must not spin forever.
    useEffect(() => {
        if (!pending) return undefined;
        const t = setTimeout(() => setPending(null), 30000);
        return () => clearTimeout(t);
    }, [pending]);

    const serverJobs = jobs.filter((j) => !dismissed.includes(j.formula_id));
    // The placeholder stands in until the server confirms a RUNNING job for this
    // formula. Testing for any status instead would let a previous run's
    // "finished" entry win for one round-trip, so re-applying a formula flashed
    // a stale success banner before the new run appeared.
    const showPending = pending
        && !serverJobs.some((j) => String(j.formula_id) === String(pending.formula_id)
            && j.status === 'running');
    const visible = showPending ? [pending, ...serverJobs] : serverJobs;
    const running = visible.filter((j) => j.status === 'running');
    const done    = visible.filter((j) => j.status !== 'running');
    const job     = running[0] || done[0];
    if (!job) return null;

    const isRunning = job.status === 'running';
    const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;

    return (
        <div style={{
            position: 'sticky', top: 'var(--MainNav-height, 56px)', zIndex: 999,
            background: isRunning ? 'linear-gradient(90deg, #6f5212 0%, #8c6a1c 55%, #b8892c 100%)' : (job.failed > 0 ? '#4c1d1d' : '#14352a'),
            color: '#fff', padding: '10px 24px',
            display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 4px 16px rgba(111,82,18,0.35)',
        }}>
            <style>{
                '@keyframes ms-pricing-spin { to { transform: rotate(360deg); } }' +
                '@keyframes ms-pricing-sweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }'
            }</style>

            {isRunning ? (
                <span style={{
                    width: 16, height: 16, border: '2px solid #f3d58a', borderTopColor: '#fff',
                    borderRadius: '50%', display: 'inline-block',
                    animation: 'ms-pricing-spin 0.7s linear infinite', flexShrink: 0,
                }} />
            ) : (
                <span style={{ fontSize: 15, flexShrink: 0 }}>{job.failed > 0 ? '⚠️' : '✅'}</span>
            )}

            <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                {!isRunning
                    ? `Formula "${job.name}" finished`
                    : job.total > 0
                        ? `Applying "${job.name}"… ${job.processed}/${job.total}`
                        : `Starting "${job.name}"…`}
            </span>

            {isRunning && (
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.22)', borderRadius: 4, height: 6, minWidth: 80, overflow: 'hidden' }}>
                    {job.total > 0 ? (
                        <div style={{ width: `${pct}%`, height: '100%', background: '#f3d58a', borderRadius: 4, transition: 'width 0.2s' }} />
                    ) : (
                        // Product count is not known yet — an indeterminate sweep
                        // reads as "working" without implying a position.
                        <div style={{ width: '35%', height: '100%', background: '#f3d58a', borderRadius: 4, animation: 'ms-pricing-sweep 1.1s ease-in-out infinite' }} />
                    )}
                </div>
            )}

            <span style={{ fontSize: 12, color: isRunning ? '#f3d58a' : '#d1fae5', whiteSpace: 'nowrap', marginLeft: isRunning ? 0 : 'auto' }}>
                {job.updated} updated
                {job.skipped ? ` · ${job.skipped} skipped` : ''}
                {job.failed ? ` · ${job.failed} failed` : ''}
            </span>

            {!isRunning && (
                <button
                    onClick={() => setDismissed((d) => [...d, job.formula_id])}
                    style={{ padding: '5px 14px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 7, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                    Dismiss
                </button>
            )}
        </div>
    );
};

export default PricingJobBanner;
